/**
 * GenerateRoutesModal — bulk-generate routes N weeks ahead.
 * Three states: input → preview → success.
 *
 * Ritme per groep (plan 2026-09-02): de preview toont per ritme-groep een
 * keuze (beide wekelijks / om de week wisselend, of wekelijks / om de week /
 * 2x per week) met de verwachte lading per route ernaast. Een andere keuze
 * draait de dry-run opnieuw; de gekozen stand gaat mee bij het aanmaken.
 * Komt de response van Apex (geen `ritmeGroepen`), dan is er geen keuze en
 * gedraagt de modal zich zoals vóór dit plan.
 */

import { useState, useMemo } from 'react';
import { useCalendarStore } from '../../stores/calendar-store';
import type { GenerateRoutesResult, RitmeStand } from '../../lib/salesforce/types';

interface Props {
  open: boolean;
  onClose: () => void;
}

type ViewState = 'input' | 'preview' | 'success';

const LADING_HOOG_PCT = 110;

/**
 * Een gateway-timeout betekent niet dat er niets gebeurd is: het aanmaken
 * loopt door tot de function gekild wordt, dus een deel van de routes staat er
 * meestal wél. Op 04-09 las een kale "API request failed (504): {}" als
 * "mislukt" terwijl er 67 routes waren aangemaakt.
 */
const TIMEOUT_STATUS = /\((504|502|408)\)/;
const TIMEOUT_WOORD = /timeout/i;
const TIMEOUT_UITLEG =
  'De aanvraag duurde te lang en is afgebroken — maar een deel van de routes is waarschijnlijk wél aangemaakt. ' +
  'Sluit dit venster, ververs de kalender en start opnieuw: de preview laat dan zien wat er nog ontbreekt. ' +
  'Opnieuw genereren is veilig, bestaande routes worden overgeslagen.';

const isTimeoutMelding = (melding: string) => TIMEOUT_STATUS.test(melding) || TIMEOUT_WOORD.test(melding);

const STAND_LABEL: Record<RitmeStand, string> = {
  beide: 'Beide wekelijks',
  wisselend: 'Om de week wisselend',
  wekelijks: 'Wekelijks',
  om_de_week: 'Om de week',
  twee_per_week: '2x per week',
};

function formatDate(yyyymmdd: string): string {
  return new Date(yyyymmdd + 'T00:00:00').toLocaleDateString('nl-NL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function GenerateRoutesModal({ open, onClose }: Props) {
  const { generateRoutes, isGenerating } = useCalendarStore();

  const [defaultStart, defaultEnd] = useMemo(() => {
    const today = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 28);
    return [toIsoDate(today), toIsoDate(end)];
  }, []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [view, setView] = useState<ViewState>('input');
  const [preview, setPreview] = useState<GenerateRoutesResult | null>(null);
  const [executed, setExecuted] = useState<GenerateRoutesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Gekozen ritme; null = nog niets gekozen, de server bepaalt (laatste run / default).
  const [ritme, setRitme] = useState<Record<string, RitmeStand> | null>(null);

  // Group toCreate by date for the preview table
  const previewByDate = useMemo(() => {
    if (!preview) return [] as Array<{ datum: string; dayName: string; routes: string[] }>;
    const map = new Map<string, { datum: string; dayName: string; routes: string[] }>();
    for (const r of preview.toCreate) {
      const existing = map.get(r.datum);
      if (existing) {
        existing.routes.push(r.routeName);
      } else {
        map.set(r.datum, { datum: r.datum, dayName: r.dayName, routes: [r.routeName] });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.datum.localeCompare(b.datum));
  }, [preview]);

  // Alleen groepen met een echte keuze (de donderdag-bonus heeft er maar één).
  const ritmeGroepen = useMemo(
    () => (preview?.ritmeGroepen ?? []).filter((g) => g.standen.length > 1),
    [preview]
  );

  if (!open) return null;

  const handleClose = () => {
    setView('input');
    setPreview(null);
    setExecuted(null);
    setError(null);
    setRitme(null);
    onClose();
  };

  const runPreview = async (keuze: Record<string, RitmeStand> | null) => {
    setError(null);
    try {
      const result = await generateRoutes(startDate, endDate, true, keuze ?? undefined);
      setPreview(result);
      // De server vult de keuze aan met de laatste run / defaults; neem dat
      // over zodat de radio's en de execute dezelfde stand dragen.
      if (result.ritmeGebruikt) setRitme(result.ritmeGebruikt);
      setView('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview mislukt');
    }
  };

  const handlePreview = () => runPreview(ritme);

  const handleRitmeChange = (groep: string, stand: RitmeStand) => {
    const volgende = { ...(ritme ?? {}), [groep]: stand };
    setRitme(volgende);
    void runPreview(volgende);
  };

  const handleExecute = async () => {
    setError(null);
    try {
      const result = await generateRoutes(startDate, endDate, false, ritme ?? undefined);
      setExecuted(result);
      setView('success');
    } catch (err) {
      const melding = err instanceof Error ? err.message : 'Aanmaken mislukt';
      setError(isTimeoutMelding(melding) ? TIMEOUT_UITLEG : melding);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Routes genereren</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {view === 'input' && 'Kies een periode — bouwt voort op het vaste ritme.'}
            {view === 'preview' && 'Kies het ritme per gebied en controleer wat er aangemaakt wordt vóór bevestiging.'}
            {view === 'success' && 'Routes zijn aangemaakt.'}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {view === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Van</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tot en met</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          )}

          {view === 'preview' && preview && (
            <div className="space-y-5">
              {ritmeGroepen.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Ritme</h4>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {ritmeGroepen.map((g) => (
                      <div key={g.groep} className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="text-sm text-gray-900 w-44">{g.leden.join(' / ')}</span>
                        {g.standen.map((stand) => (
                          <label key={stand} className="flex items-center gap-1.5 text-sm text-gray-700">
                            <input
                              type="radio"
                              name={`ritme-${g.groep}`}
                              checked={(ritme?.[g.groep] ?? g.stand) === stand}
                              disabled={isGenerating}
                              onChange={() => handleRitmeChange(g.groep, stand)}
                              className="text-indigo-600 focus:ring-indigo-500"
                            />
                            {STAND_LABEL[stand]}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    De laatst gekozen stand is de default van de volgende run. Om de week = twee weken vraag op één rit; de ladingtabel rekent het voor.
                  </p>
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Aan te maken routes ({preview.toCreate.length} routes / {previewByDate.length} dagen)
                  {isGenerating && <span className="ml-2 text-xs font-normal text-gray-500">Herberekenen…</span>}
                </h4>
                {previewByDate.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Geen nieuwe routes — alles bestaat al of valt op een feestdag.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                        <tr>
                          <th className="px-3 py-2 text-left">Datum</th>
                          <th className="px-3 py-2 text-left">Dag</th>
                          <th className="px-3 py-2 text-left">Routes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {previewByDate.map((d) => (
                          <tr key={d.datum}>
                            <td className="px-3 py-2 text-gray-900">{formatDate(d.datum)}</td>
                            <td className="px-3 py-2 text-gray-600 capitalize">{d.dayName}</td>
                            <td className="px-3 py-2 text-gray-700">{d.routes.join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {(preview.ladingPerRoute?.length ?? 0) > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Verwachte lading per rit</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                        <tr>
                          <th className="px-3 py-2 text-left">Route</th>
                          <th className="px-3 py-2 text-right">Ophalen / wk</th>
                          <th className="px-3 py-2 text-right">Afleveren / wk</th>
                          <th className="px-3 py-2 text-right">Ritten</th>
                          <th className="px-3 py-2 text-right">Verwacht / cap</th>
                          <th className="px-3 py-2 text-right">%</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[...(preview.ladingPerRoute ?? [])]
                          .sort((a, b) => (b.ladingPct ?? 0) - (a.ladingPct ?? 0))
                          .map((l) => {
                            const hoog = (l.ladingPct ?? 0) > LADING_HOOG_PCT;
                            return (
                              <tr key={l.routeNaam} className={hoog ? 'bg-amber-50' : undefined}>
                                <td className={`px-3 py-1.5 ${hoog ? 'text-amber-900 font-medium' : 'text-gray-900'}`}>{l.routeNaam}</td>
                                <td className="px-3 py-1.5 text-right text-gray-600">{l.stopsPerWeekOphalen}</td>
                                <td className="px-3 py-1.5 text-right text-gray-600">{l.stopsPerWeekAfleveren}</td>
                                <td className="px-3 py-1.5 text-right text-gray-600">{l.ritten} ({l.wekenPerRit} wk/rit)</td>
                                <td className="px-3 py-1.5 text-right text-gray-700">
                                  {l.ladingOphalen}/{l.capOphalen ?? '?'} · {l.ladingAfleveren}/{l.capAfleveren ?? '?'}
                                </td>
                                <td className={`px-3 py-1.5 text-right font-medium ${hoog ? 'text-amber-900' : 'text-gray-900'}`}>
                                  {l.ladingPct ?? '–'}{l.ladingPct != null ? '%' : ''}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Stops per week over de laatste 8 gereden ritten × weken per rit in deze periode, tegen de capaciteit van de routedefinitie. Oranje boven {LADING_HOOG_PCT}%.
                  </p>
                </div>
              )}

              {preview.skipped.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-amber-900 mb-2">
                    Overgeslagen ({preview.skipped.length})
                  </h4>
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-1">
                    {preview.skipped.map((s, i) => (
                      <div key={i} className="text-sm text-amber-900">
                        <span className="font-medium">{formatDate(s.datum)}</span> —{' '}
                        {s.reason === 'HOLIDAY' ? 'Feestdag' : 'Bestaande routes'}: {s.detail}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {preview.warnings.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-900 mb-2">
                    Waarschuwingen ({preview.warnings.length})
                  </h4>
                  <div className="border border-red-200 bg-red-50 rounded-lg p-3 space-y-1">
                    {preview.warnings.map((w, i) => (
                      <div key={i} className="text-sm text-red-900">
                        <span className="font-medium">{formatDate(w.datum)}</span> — {w.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {view === 'success' && executed && (
            <div className="space-y-3">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-900">
                  ✓ {executed.toCreate.length} routes aangemaakt
                </p>
              </div>
              {executed.skipped.length > 0 && (
                <p className="text-sm text-gray-600">
                  {executed.skipped.length} dag(en) overgeslagen.
                </p>
              )}
              {executed.warnings.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  {executed.warnings.length} waarschuwing(en) — controleer of feestdagen handmatig elders geplaatst moeten worden.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          {view === 'input' && (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handlePreview}
                disabled={isGenerating || !startDate || !endDate}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {isGenerating ? 'Bezig…' : 'Preview'}
              </button>
            </>
          )}

          {view === 'preview' && (
            <>
              <button
                type="button"
                onClick={() => setView('input')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Terug
              </button>
              <button
                type="button"
                onClick={handleExecute}
                disabled={isGenerating || !preview || preview.toCreate.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {isGenerating ? 'Aanmaken…' : 'Bevestig en maak aan'}
              </button>
            </>
          )}

          {view === 'success' && (
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              Sluiten
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
